import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  LogIn,
  X as CancelIcon,
} from "lucide-react";
import { spaceManagementService } from "../../services/host-apiServices/spaceManagementService";
import * as localService from "../../services/localService";
import { Card, CardContent } from "../../shadcn/ui/card";
import { Button } from "../../shadcn/ui/button";
import logger from "../../utils/logger";

/**
 * BookingDetails - Public page to display booking information
 * Accessed via CTA link from booking confirmation email
 * Route: /booking/:bookingId
 */
const BookingDetails = () => {
  const { bookingId } = useParams();
  const history = useHistory();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        setError("Booking ID is required");
        setLoading(false);
        return;
      }

      // Redirect to login with return URL if not authenticated
      const redirectToLogin = () => {
        const returnUrl = `/booking/${bookingId}`;
        history.push(`/signin?redirect=${encodeURIComponent(returnUrl)}`);
      };

      // Check if user is authenticated before making API call
      if (!localService.isAuthenticated()) {
        redirectToLogin();
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await spaceManagementService.getBookingById(Number(bookingId));

        if (response?.status === 200 && response?.data) {
          setBooking(response.data);
        } else {
          setError("Booking not found");
        }
      } catch (err) {
        logger.error("Error fetching booking:", err);
        if (err?.response?.status === 401) {
          // Session expired or invalid - redirect to login
          redirectToLogin();
        } else if (err?.response?.status === 404) {
          setError("Booking not found");
        } else if (err?.response?.status === 403) {
          setError("You don't have permission to view this booking");
        } else {
          setError("Failed to load booking details. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, history]);

  // Get booking status for display
  const getBookingStatus = () => {
    if (!booking?.status) return "pending";
    return booking.status.toLowerCase();
  };

  const getStatusColor = () => {
    const status = getBookingStatus();
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
      case "pending_payment":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "checked_in":
      case "active":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "completed":
      case "checked_out":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "no_show":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = () => {
    const status = getBookingStatus();
    switch (status) {
      case "confirmed":
        return <CheckCircle className="w-4 h-4" />;
      case "checked_in":
      case "active":
        return <LogIn className="w-4 h-4" />;
      case "pending":
      case "pending_payment":
        return <Clock className="w-4 h-4" />;
      case "cancelled":
        return <CancelIcon className="w-4 h-4" />;
      case "no_show":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const getStatusLabel = () => {
    const status = getBookingStatus();
    return status.replace(/_/g, " ");
  };

  const formatDate = date => {
    if (!date) return "N/A";
    const validDate = date instanceof Date ? date : new Date(date);
    if (isNaN(validDate.getTime())) return "Invalid Date";

    return validDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = date => {
    if (!date) return "N/A";
    const validDate = date instanceof Date ? date : new Date(date);
    if (isNaN(validDate.getTime())) return "Invalid Time";

    return validDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTimeRange = (start, end) => {
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  // Extract space details from booking
  const getSpaceDetails = () => {
    const spaceInstance = booking?.spaceInstance;
    const space = spaceInstance?.space || booking?.space;
    const location = spaceInstance?.location;
    const building = location?.buildingAddress;

    return {
      name: space?.name || spaceInstance?.title || "Space",
      description: space?.description || spaceInstance?.description,
      image: space?.media?.images?.[0]?.url || space?.media?.images?.[0] || spaceInstance?.media?.images?.[0]?.url,
      capacity: space?.capacity || spaceInstance?.listingDetails?.maxCapacity,
      address: building?.streetAddress || location?.name || booking?.location,
      city: building?.city,
      buildingName: location?.name || building?.buildingName,
    };
  };

  // Extract booker details
  const getBookerDetails = () => {
    const bookedBy = booking?.bookedBy;
    return {
      name: bookedBy ? `${bookedBy.firstName || ""} ${bookedBy.lastName || ""}`.trim() : "Guest",
      email: bookedBy?.emailAddress || booking?.email,
      avatar: bookedBy?.photoUrl,
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Booking</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button variant="outline" onClick={() => history.push("/")} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const spaceDetails = getSpaceDetails();
  const bookerDetails = getBookerDetails();

  // Handle back navigation - always go to reservations screen (Booking.jsx)
  const handleBack = () => {
    history.push("/reservations");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <Button variant="ghost" onClick={handleBack} className="mb-4 gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </Button>

        {/* Main Card */}
        <Card className="overflow-hidden shadow-lg">
          {/* Space Image Header */}
          {spaceDetails.image ? (
            <div className="relative h-40 sm:h-48 bg-gray-200">
              <img
                src={spaceDetails.image}
                alt={spaceDetails.name}
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.onerror = null;
                  e.target.src = "/img/placeholder-space.jpg";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                <h1 className="text-xl sm:text-2xl font-bold text-white">{spaceDetails.name}</h1>
                {spaceDetails.buildingName && (
                  <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{spaceDetails.buildingName}</span>
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="relative h-28 sm:h-32 bg-gradient-to-br from-blue-500 to-blue-700">
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                <h1 className="text-xl sm:text-2xl font-bold text-white">{spaceDetails.name}</h1>
              </div>
            </div>
          )}

          <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Status Badge */}
            <div className="flex justify-center">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getStatusColor()}`}>
                {getStatusIcon()}
                <span className="font-semibold capitalize">{getStatusLabel()}</span>
              </div>
            </div>

            {/* Booking Reference */}
            <div className="text-center">
              <p className="text-sm text-gray-500">Booking Reference</p>
              <p className="text-lg font-mono font-semibold text-gray-900">#{booking?.id}</p>
            </div>

            {/* Date and Time */}
            <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-100">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Date & Time</h3>
                  <p className="text-gray-700 text-sm sm:text-base">{formatDate(booking?.startTime)}</p>
                  <p className="text-blue-700 font-medium flex items-center gap-1 mt-1 text-sm sm:text-base">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    {formatTimeRange(booking?.startTime, booking?.endTime)}
                  </p>
                </div>
              </div>
            </div>

            {/* Location */}
            {spaceDetails.address && (
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Location</h3>
                    <p className="text-gray-700 text-sm sm:text-base break-words">{spaceDetails.address}</p>
                    {spaceDetails.city && <p className="text-gray-500 text-sm">{spaceDetails.city}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Booked By */}
            <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {bookerDetails.avatar ? (
                    <img
                      src={bookerDetails.avatar}
                      alt={bookerDetails.name}
                      className="w-full h-full object-cover"
                      onError={e => {
                        e.target.onerror = null;
                        e.target.style.display = "none";
                        e.target.parentNode.innerHTML = `<span class="text-white font-semibold text-sm sm:text-base">${bookerDetails.name
                          .split(" ")
                          .map(n => n[0])
                          .join("")}</span>`;
                      }}
                    />
                  ) : (
                    <span className="text-white font-semibold text-sm sm:text-base">
                      {bookerDetails.name
                        .split(" ")
                        .map(n => n[0])
                        .join("")}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Booked By</h3>
                  <p className="text-gray-700 text-sm sm:text-base">{bookerDetails.name}</p>
                  {bookerDetails.email && <p className="text-gray-500 text-sm truncate">{bookerDetails.email}</p>}
                </div>
              </div>
            </div>

            {/* Attendees */}
            {booking?.emails && booking.emails.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Attendees</h3>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {booking.emails.map((email, idx) => (
                        <span
                          key={idx}
                          className="text-xs sm:text-sm bg-white border border-gray-200 px-2 sm:px-3 py-1 rounded-full text-gray-600 truncate max-w-[200px]"
                        >
                          {email}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Capacity */}
            {spaceDetails.capacity && (
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Capacity</h3>
                    <p className="text-gray-700 text-sm sm:text-base">Up to {spaceDetails.capacity} people</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {booking?.notes && (
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Notes</h3>
                <p className="text-gray-700 text-sm">{booking.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Need help? Contact us at{" "}
          <a href="mailto:support@spacia.africa" className="text-blue-600 hover:underline">
            support@spacia.africa
          </a>
        </p>
      </div>
    </div>
  );
};

export default BookingDetails;
